import React, { useState } from 'react';

// A compact, tabbed render of the app's real summary output, shown "on the laptop"
// during the entrance so a visitor sees genuine product. Four sections — why /
// try / what to say / when to escalate — using the clearly-fictional J.S./Grade 5
// example. Tabs keep every section readable without shrinking the text. Marked
// "SAMPLE" so it is never mistaken for a real student record.

type TabKey = 'why' | 'try' | 'say' | 'escalate';
const TABS: { key: TabKey; label: string }[] = [
  { key: 'why', label: 'Why' },
  { key: 'try', label: 'Try today' },
  { key: 'say', label: 'What to say' },
  { key: 'escalate', label: 'Escalate' },
];

const TRY = [
  'Offer a choice of where to start: one sentence out loud before writing it.',
  'Break the blank page into a 3-line frame so starting feels smaller.',
  'Give a quiet 2-minute warm-up with no grading pressure.',
];

const label: React.CSSProperties = { fontSize: 10.5, fontWeight: 700, letterSpacing: 0.6, color: 'var(--teal)', marginBottom: 4 };
const body: React.CSSProperties = { fontSize: 12.5, lineHeight: 1.45, color: 'var(--charcoal)' };

export default function LaptopPreview() {
  const [tab, setTab] = useState<TabKey>('why');
  return (
    <div style={{ textAlign: 'left' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 5 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span className="mono" style={{ fontSize: 15, fontWeight: 500, color: 'var(--teal)' }}>J.S.</span>
          <span style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>Grade 5</span>
        </div>
        <span style={{ background: 'var(--amber-light)', color: '#b87800', fontSize: 9.5, fontWeight: 700, letterSpacing: 0.4, padding: '2px 8px', borderRadius: 20 }}>SAMPLE</span>
      </div>

      <p style={{ fontSize: 12, color: 'var(--text-secondary)', fontStyle: 'italic', marginBottom: 7 }}>
        “Shuts down whenever independent writing begins.”
      </p>

      <div className="tabs" style={{ marginBottom: 8 }}>
        {TABS.map(t => (
          <button key={t.key} className={`tab${tab === t.key ? ' active' : ''}`} onClick={() => setTab(t.key)} aria-pressed={tab === t.key}>{t.label}</button>
        ))}
      </div>

      {tab === 'why' && (
        <div>
          <p style={label}>WHAT MAY BE DRIVING IT</p>
          <p style={body}>A blank page can read as a threat to a nervous system that's already unsure of success. The shutdown is often protection, not defiance — going still to avoid a task that feels impossible to start.</p>
        </div>
      )}
      {tab === 'try' && (
        <div>
          <p style={label}>THREE THINGS TO TRY TODAY</p>
          {TRY.map((t, i) => (
            <div key={i} style={{ display: 'flex', gap: 9, alignItems: 'flex-start', marginBottom: 6 }}>
              <span style={{ width: 18, height: 18, borderRadius: '50%', background: 'var(--amber)', color: '#fff', fontSize: 10.5, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{i + 1}</span>
              <p style={{ ...body, fontSize: 12 }}>{t}</p>
            </div>
          ))}
        </div>
      )}
      {tab === 'say' && (
        <div>
          <p style={label}>WHAT TO SAY</p>
          <div style={{ background: 'var(--teal-light)', borderRadius: 10, padding: '12px 14px' }}>
            <p style={{ ...body, fontStyle: 'italic' }}>“You don't have to get it right — just get it started. Say your first sentence out loud and I'll write it with you.”</p>
          </div>
        </div>
      )}
      {tab === 'escalate' && (
        <div>
          <p style={{ ...label, color: 'var(--coral)' }}>WHEN TO ESCALATE</p>
          <p style={body}>If the shutdowns spread to most of the day, come with signs of hopelessness, or don't ease with support over a few weeks, loop in your counselor or student-support team.</p>
        </div>
      )}
    </div>
  );
}
